import type {
	CurrencyInputAnswer,
	DocumentUploadAnswer,
	MultiSelectAnswer,
	NumberInputAnswer,
	ShortTextAnswer,
	SingleSelectAnswer,
} from '#api/customQuestionnaire/types/customQuestionnaire'

import { getIn } from 'formik'

import type {
	FormikValues,
	QuestionDefinition,
	UploadedFileMetadata,
} from '../..'

import { activeSubquestions } from '../questionnaireConfig/utils'

type Answer =
	| SingleSelectAnswer
	| MultiSelectAnswer
	| ShortTextAnswer
	| NumberInputAnswer
	| CurrencyInputAnswer
	| DocumentUploadAnswer

export const mapToApiAnswers = (
	values: FormikValues,
	questions: QuestionDefinition[],
	currency: string,
	documentUploads?: Record<string, UploadedFileMetadata[]>,
): Answer[] => {
	const result: Answer[] = []

	questions.forEach((question) => {
		mapQuestion(values, question, result, currency, documentUploads)
	})

	return result
}

const getCommonProperties = (question: QuestionDefinition) => ({
	questionId: question.id,
	...(question.slug && { questionSlug: question.slug }),
})

const mapQuestion = (
	values: FormikValues,
	question: QuestionDefinition,
	result: Answer[],
	currency: string,
	documentUploads?: Record<string, UploadedFileMetadata[]>,
) => {
	const answer = getIn(values, question.formFieldKey)

	if (question.questionType === 'singleSelect' && answer) {
		result.push({
			'$type': 'SingleSelectQuestion',
			questionType: 'SingleSelectQuestion',
			...getCommonProperties(question),
			value: answer,
		})

		activeSubquestions(values, question)?.forEach((subQuestion) =>
			mapQuestion(values, subQuestion, result, currency, documentUploads),
		)
	}

	if (
		question.questionType === 'multiSelect' &&
		answer &&
		Array.isArray(answer)
	) {
		result.push({
			'$type': 'MultiSelectQuestion',
			questionType: 'MultiSelectQuestion',
			...getCommonProperties(question),
			value: answer,
		})

		activeSubquestions(values, question)?.forEach((subQuestion) =>
			mapQuestion(values, subQuestion, result, currency, documentUploads),
		)
	}

	if (question.questionType === 'textInput' && answer) {
		result.push({
			'$type': 'ShortTextQuestion',
			questionType: 'ShortTextQuestion',
			...getCommonProperties(question),
			value: answer,
		})
	}

	if (
		question.questionType === 'numberInput' &&
		answer !== null &&
		answer !== undefined
	) {
		result.push({
			'$type': 'NumberInputQuestion',
			questionType: 'NumberInputQuestion',
			...getCommonProperties(question),
			value: answer,
		})
	}

	if (
		question.questionType === 'currencyInput' &&
		answer !== null &&
		answer !== undefined &&
		currency
	) {
		result.push({
			'$type': 'CurrencyInputQuestion',
			questionType: 'CurrencyInputQuestion',
			...getCommonProperties(question),
			value: {
				amount: answer,
				currency,
			},
		})
	}

	if (question.questionType === 'documentUpload') {
		const files = documentUploads?.[question.formFieldKey]
		if (files && files.length > 0) {
			result.push({
				'$type': 'DocumentUploadQuestion',
				questionType: 'DocumentUploadQuestion',
				...getCommonProperties(question),
				value: files.map((file) => ({
					fileId: file.fileId,
					fileName: file.fileName,
				})),
			})
		}
	}
}
